import {
  ImmutablePiecewiseFunction,
  ParametricFunctionCallback,
  PiecewiseFunction,
} from '@fullstory/piecewise-function';
import type { RawEvent, TimeStampMs } from '@fullstory/recording-api';
import type { FramePath } from '@fullstory/renderer-shared';

/**
 * A timeline for a time-varying value derived from a stream of
 * {@link RawEvent}s.
 *
 * This subclass extends {@link PiecewiseFunction} by adding helper methods to
 * make it easy to add {@link RawEvent} values to the timeline. Each event is
 * interpreted as a change in value; the value will stay the same from that
 * point forward until another event triggers another change.
 */
export class Timeline<Value> extends PiecewiseFunction<Value> {
  /**
   * Add a value change to the timeline.
   *
   * @param event - The {@link RawEvent} that generated the value change. The
   * {@link RawEvent#When} property determines when the value change happens on
   * the timeline.
   * @param value - The new value.
   */
  addEventWithValue(event: RawEvent, value: Value | undefined): void {
    // Add this value to the timeline and extend it to infinity. This models the
    // value staying the same until some other event changes it. We don't give a
    // priority boost for frame depth because we want any future event to
    // override this one, even if it occurs in a different frame. Compare this
    // to addEventWithCurve(); in essence, the value change we're modeling in
    // this method is a curve with zero duration, so the only thing that's
    // getting added to the timeline is the tail.
    this.add(value, event.When, Number.POSITIVE_INFINITY, eventPriority(event.When, undefined));
  }

  /**
   * Add a series of value changes to the timeline based on a parametric
   * function. This is normally a Hermite curve (hence the method name).
   *
   * @param event - The {@link RawEvent} that generated the value changes. The
   * {@link RawEvent#When} property determines when the value changes start on
   * the timeline.
   * @param curveEnd - The point at which the value changes end on the timeline.
   * @param curveFunction - The parametric function defining the value changes.
   * @param frameId - If provided, the frame that generated this event. This is
   * used to set the priority of the value changes - values from more deeply
   * nested frames take precedence over values from shallower frames. See
   * {@link eventPriority} for more details.
   */
  addEventWithCurve(
    event: RawEvent,
    curveEnd: TimeStampMs,
    curveFunction: { at: ParametricFunctionCallback<Value> },
    frameId?: FramePath,
  ): void {
    // Add the series of values to the timeline. When calculating the priority
    // of these values, we incorporate the nesting depth of the frame that
    // generated 'event'. We add one to the nesting depth to ensure that, all
    // else being equal, the "tails" we add below will have lower priority.
    const frameDepth = frameId ? frameId.components.length + 1 : undefined;
    const curvePriority = eventPriority(event.When, frameDepth);
    this.add(curveFunction, event.When, curveEnd, curvePriority);

    // Extend the final value in the series to infinity. This models the value
    // staying the same until some other event changes it. We don't give a
    // priority boost for frame depth for the "tail" because we want to ensure
    // that any future event will take precedence over it, even if it happens in
    // a different frame.
    const tailValue = curveFunction.at(1, curveEnd, event.When, curveEnd);
    const tailPriority = eventPriority(curveEnd, undefined);
    this.add(tailValue, curveEnd, Number.POSITIVE_INFINITY, tailPriority);
  }
}

export type ImmutableTimeline<Value> = ImmutablePiecewiseFunction<Value>;

/**
 * Calculate the priority that an event should have when added to a
 * {@link Timeline}. Events which happen later have higher priority; this is so
 * that overlapping events can be added in any order with the same result.
 *
 * For many events, like mouse movements, events are dispatched to the most
 * deeply nested frame that can receive the event. (For example, if you mouse
 * over an iframe, the iframe receives the mouse move events and not the
 * containing document.) To model this dispatch behavior when events from
 * different frames overlap, a 'frameDepth' can optionally be provided which
 * will give events from more deeply nested frames higher priority. If no
 * 'frameDepth' is provided, the priority is calculated as if the event occurred
 * in the root document.
 *
 * @param eventWhen - {@link RawEvent#When} value of an event that will live on
 * a {@link Timeline}.
 * @param frameDepth - The nesting level of the frame where 'event' was
 * recorded, or 'undefined' if frame nesting level should have no effect on the
 * priority of this event.
 * @return the priority that the provided {@link RawEvent} should have, based on
 * the provided parameters.
 */
export function eventPriority(eventWhen: TimeStampMs, frameDepth: number | undefined): number {
  // Extract 40 bits of 'event.When'. That's enough resolution to cover a session
  // lasting 34 years, so it should be plenty. This is a little bit awkward
  // because JavaScript's bitwise operators implicitly discard all but the low
  // 32 bits of a number, so we have to use division as a substitute for right
  // shift to get the high 8 bits.
  const lowWhenBits = eventWhen >>> 0;
  const highWhenBits = (eventWhen / 2 ** 8) >>> 24;

  // Extract 12 bits of the frame depth. That lets us support a depth of up to
  // 4096, which is far beyond what any realistic page will use. We use 12 bits
  // so that we stay comfortably within the 53 bits of significand provided by
  // JavaScript number; if we went beyond that, integer math would no longer be
  // precise.
  const frameDepthBits = (frameDepth ?? 0) & 0x0fff;

  // Merge the high bits, with the frame depth bits on the left so that they
  // matter more than the 'event.When' bits, and left shift them to their final
  // position using multiplication.
  const highBits = ((frameDepthBits << 8) | highWhenBits) * 2 ** 32;

  // To get the final priority, add the high and low 32-bit words. There's
  // guaranteed to be no overflow, so addition is equivalent to OR.
  return highBits + lowWhenBits;
}
